// Mounted units (2026-10-27, horses round): a rigged horse with a rigged rider on its back, in place of the fused
// rider-and-horse models that only hopped as one rigid piece. The horse is a Meshy horse alone, fitted to the CC0
// Mesh2Motion horse rig in Blender (scripts/fit_horse.py), with that rig's clips; the rider is the class's own file, a
// Meshy humanoid drawn without his horse, seated here: put on the saddle, bolted to the horse's spine bone so its gait
// carries him, his legs posed astride, his own clips kept for the upper body. The Egyptian chariot is two of these
// horses in front of its car (the old fused model with the horses and crew cut away) with a driver and an archer
// standing in it. Everything is baked like any unit (baked.ts); a gait plays by ground speed (gaitRow).
import * as THREE from 'three';
import { standClip } from './props';

/** A mounted class (ARMY_ART.mounts, by the class's file): its horse file (clips walk, trot, run, idle, butt and death,
 *  named by scripts/pack_unit.ts --own from the Mesh2Motion rig's), the horse's height in rider bodies (feet
 *  to skull top, standing), and for the chariot the crew files standing in the car (the class's file is then the car).
 *  `attack`: the horse's clip under the rider's attack (a head butt for a rider who strikes, a stand for an archer). */
export interface Mount { horse: string; size?: number; attack?: 'butt' | 'stand'; crew?: string[] }

/** Leg bones a seated rider's clips may not move (his legs keep the seat) and the hips' slide. */
const LEG = /^(Left|Right)(UpLeg|Leg|Foot|ToeBase)\b/;
/** The default horse: as tall to its ears as 1.15 riders standing (the fused models' horses measured 1.1 to 1.25). */
export const HORSE_H = 1.15;

/** Points over a mesh's surface in its rest pose, about `step` of its size apart (each triangle sampled on a grid fine
 *  enough for that): a match file's few thousand triangles leave whole stretches of the back with no vertex on the
 *  midline, and a vertex-only top line read those as dips (2026-10-27, the pony seated its rider at 31% of its height).
 *  Positions go through the skin: a quantized file keeps its scale in the inverse bind matrices, not the node. */
export function surface(root: THREE.Object3D, step: number) {
  root.updateMatrixWorld(true);
  const tris: THREE.Vector3[][] = [];
  root.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isMesh) return;
    const at = (i: number) => m.getVertexPosition(i, new THREE.Vector3()).applyMatrix4(m.matrixWorld);
    const g = m.geometry, n = g.index ? g.index.count : g.attributes.position.count, idx = (k: number) => (g.index ? g.index.getX(k) : k);
    for (let k = 0; k < n; k += 3) tris.push([at(idx(k)), at(idx(k + 1)), at(idx(k + 2))]);
  });
  const box = new THREE.Box3();
  for (const t of tris) for (const p of t) box.expandByPoint(p);
  const d = step * box.getSize(new THREE.Vector3()).length(), pts: THREE.Vector3[] = [];
  for (const [a, b, c] of tris) {
    const n = Math.max(1, Math.ceil(Math.max(a.distanceTo(b), b.distanceTo(c), c.distanceTo(a)) / d));
    for (let i = 0; i <= n; i++) for (let j = 0; i + j <= n; j++) {
      const u = i / n, w = j / n;
      pts.push(new THREE.Vector3().addScaledVector(a, 1 - u - w).addScaledVector(b, u).addScaledVector(c, w));
    }
  }
  return pts;
}

/** Where the rider sits, measured on the horse's mesh in its rest pose (the horse's own space after `placed`): the
 *  low point of the back's top line between the shoulders and the hips (the saddle's seat), and the barrel's half
 *  width under it (90th percentile, so a stirrup or a cloth's corner does not count). */
export function saddleOf(horse: THREE.Object3D) {
  const pts = surface(horse, 0.012);
  const box = new THREE.Box3().setFromPoints(pts), H = box.max.y - box.min.y, W = box.max.x - box.min.x, cx = (box.min.x + box.max.x) / 2;
  // the legs, low down: the front pair and the hind pair are the two ends of their z spread
  const legs = pts.filter((p) => p.y < box.min.y + 0.2 * H).map((p) => p.z).sort((a, b) => a - b);
  const back = legs[Math.floor(legs.length * 0.1)], front = legs[Math.floor(legs.length * 0.9)];
  // the top line near the midline, in bins over the saddle's stretch (40 to 75% of the way from the hind legs to the
  // front: the croup behind it and the withers ahead of it are higher than the seat, the loin just behind the cantle can
  // be lower). A bin is the median of itself and its neighbours, so one gap in the shell (a cloth's edge on the
  // midline) is not the seat (2026-10-27, the Knights horse sat its rider on the croup).
  const n = 8, raw = new Array<number>(n + 2).fill(-Infinity), z0 = back + 0.4 * (front - back), z1 = back + 0.75 * (front - back), dz = (z1 - z0) / n;
  for (const p of pts) {
    if (Math.abs(p.x - cx) > 0.2 * W) continue;
    const k = Math.floor((p.z - z0) / dz) + 1;
    if (k >= 0 && k < n + 2) raw[k] = Math.max(raw[k], p.y);
  }
  const top = Array.from({ length: n }, (_, j) => {
    const near = [raw[j], raw[j + 1], raw[j + 2]].filter(Number.isFinite).sort((a, b) => a - b);
    return Number.isFinite(raw[j + 1]) ? near[(near.length - 1) >> 1] : Infinity;
  });
  let k = 0;
  for (let j = 1; j < n; j++) if (top[j] < top[k]) k = j;
  if (!Number.isFinite(top[k])) throw new Error('no back to seat a rider on: the horse has no points over its middle');
  const z = z0 + (k + 0.5) * dz, y = top[k];
  // the barrel's half width under the seat, in bands of 5% of the height down to 30% (a rounded barrel is narrower at
  // the knee, just under the seat, than at its middle): each band's 90th percentile, so a stirrup or a cloth's corner
  // does not count
  const near = pts.filter((p) => Math.abs(p.z - z) < 0.1 * (front - back)), bands: number[] = [], band = 0.05 * H;
  for (let b = 0; y - (b + 1) * band > box.min.y + 0.3 * H; b++) {
    const xs = near.filter((p) => p.y <= y - b * band && p.y > y - (b + 1) * band).map((p) => Math.abs(p.x - cx)).sort((a, b) => a - b);
    bands.push(xs.length ? xs[Math.floor(xs.length * 0.9)] : b ? bands[b - 1] : W / 2);
  }
  if (!bands.length) bands.push(W / 2);
  /** The barrel's half width at height h (the seat's band above the seat, the lowest band's below it). */
  const halfAt = (h: number) => bands[THREE.MathUtils.clamp(Math.floor((y - h) / band), 0, bands.length - 1)];
  return { at: new THREE.Vector3(cx, y, z), half: Math.max(...bands), halfAt, height: H, length: box.max.z - box.min.z };
}

/** The bone the saddle rides on: the horse's spine bone nearest the seat, so the back's rise and fall carry him. */
export function saddleBone(horse: THREE.Object3D, seat: THREE.Vector3) {
  let best: THREE.Object3D | null = null, d = Infinity;
  const w = new THREE.Vector3();
  horse.traverse((o) => {
    if (!(o as THREE.Bone).isBone || !/^spine_\d$/.test(o.name)) return;
    const dz = Math.abs(o.getWorldPosition(w).z - seat.z);
    if (dz < d) { d = dz; best = o; }
  });
  if (!best) throw new Error('the horse has no spine bones to seat a rider on');
  return best as THREE.Object3D;
}

/** A rider's clips for the saddle: no leg tracks (his legs keep the seat) and no hips slide (he stays on it). The
 *  ride stance is his walk held at its stance (props.ts standClip), with his arms as he carries his weapon. */
export function riderClips(rider: THREE.Object3D, clips: THREE.AnimationClip[]) {
  const strip = (c: THREE.AnimationClip) => { c.tracks = c.tracks.filter((t) => !LEG.test(t.name) && !/^Hips\.position$/.test(t.name)); return c; };
  const walk = clips.find((c) => c.name === 'walk') ?? clips[0];
  if (!walk) throw new Error('a rider needs a walk clip to take his seat from');
  const ride = strip(standClip(rider, walk, 'ride'));
  const attack = clips.find((c) => c.name === 'attack');
  return attack ? [ride, strip(attack)] : [ride];
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _q = new THREE.Quaternion(), _p = new THREE.Quaternion();
/** Turns a bone (in world space, about its head) so the direction from its head to `child`'s head becomes `dir`. */
function aimBone(bone: THREE.Object3D, child: THREE.Object3D, dir: THREE.Vector3) {
  bone.updateMatrixWorld(true);
  const from = child.getWorldPosition(_a).sub(bone.getWorldPosition(_b)).normalize();
  const turn = _q.setFromUnitVectors(from, dir.clone().normalize());
  const world = bone.getWorldQuaternion(new THREE.Quaternion()).premultiply(turn);
  bone.quaternion.copy(bone.parent!.getWorldQuaternion(_p).invert().multiply(world));
  bone.updateMatrixWorld(true);
}

/** How thick a bone's flesh is: the 75th percentile distance from the bone's segment (head to child's head) of the
 *  rest-pose vertices it mostly moves. */
function thickness(rider: THREE.Object3D, bone: THREE.Object3D, child: THREE.Object3D) {
  const a = bone.getWorldPosition(new THREE.Vector3()), b = child.getWorldPosition(new THREE.Vector3()), d: number[] = [];
  const seg = new THREE.Line3(a, b), v = new THREE.Vector3(), c = new THREE.Vector3();
  rider.traverse((o) => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh) return;
    const k = m.skeleton.bones.indexOf(bone as THREE.Bone), g = m.geometry.attributes;
    if (k < 0) return;
    for (let i = 0; i < g.position.count; i++) {
      let w = 0;
      for (let j = 0; j < 4; j++) if (g.skinIndex.getComponent(i, j) === k) w += g.skinWeight.getComponent(i, j);
      if (w < 0.5) continue;
      m.getVertexPosition(i, v).applyMatrix4(m.matrixWorld);
      d.push(seg.closestPointToPoint(v, true, c).distanceTo(v));
    }
  });
  d.sort((x, y) => x - y);
  return d.length ? d[Math.floor(d.length * 0.75)] : a.distanceTo(b) * 0.3;
}

/** The leg-pose fix (2026-10-27; desktop run 5 of the horse test showed no leg below the saddle on the near side: the
 *  thighs and shins were inside the barrel). Each thigh is aimed so the outside of the knee clears the barrel's half
 *  width under the seat by the thigh's own thickness, the rest of its length split forward and down; the shin hangs
 *  from the knee down the horse's side, a little back and out. Works in world space on a rider already placed on the
 *  horse. Returns how far the knee joint, and the shin at its closest, sit outside the barrel (rider units), for the tests. */
export function seatLegs(rider: THREE.Object3D, center: THREE.Vector3, halfAt: (y: number) => number) {
  rider.updateMatrixWorld(true);
  const out: { side: string; knee: number; shin: number }[] = [];
  for (const side of ['Left', 'Right']) {
    const up = rider.getObjectByName(`${side}UpLeg`), knee = rider.getObjectByName(`${side}Leg`), foot = rider.getObjectByName(`${side}Foot`);
    if (!up || !knee || !foot) throw new Error(`the rider has no ${side} leg bones`);
    const hip = up.getWorldPosition(new THREE.Vector3()), lt = knee.getWorldPosition(new THREE.Vector3()).distanceTo(hip);
    const r = thickness(rider, up, knee), s = Math.sign(hip.x - center.x) || (side === 'Left' ? 1 : -1);
    // across: from the hip out to the barrel's side at the knee's height plus the thigh's own flesh, at most 0.92 of
    // the thigh; twice, since how far across sets how far down the knee comes, and so the width it must clear
    const pitch = THREE.MathUtils.degToRad(40), off = Math.abs(hip.x - center.x);
    let dx = 0.6 * lt;
    for (let pass = 0; pass < 2; pass++) {
      const drop = Math.sqrt(lt * lt - dx * dx) * Math.cos(pitch);
      dx = Math.min(0.92 * lt, Math.max(0.25 * lt, halfAt(hip.y - drop) + r - off));
    }
    const rest = Math.sqrt(lt * lt - dx * dx);
    aimBone(up, knee, new THREE.Vector3(s * dx, -rest * Math.cos(pitch), rest * Math.sin(pitch)));
    // the shin hangs down the side, out as far as the barrel at the foot's height needs (a little at least)
    const k = knee.getWorldPosition(new THREE.Vector3()), ls = foot.getWorldPosition(new THREE.Vector3()).distanceTo(k);
    const out_ = THREE.MathUtils.clamp((halfAt(k.y - 0.95 * ls) + 0.6 * r - Math.abs(k.x - center.x)) / ls, 0.12, 0.6);
    aimBone(knee, foot, new THREE.Vector3(s * out_, -1, -0.22));
    // the least room outside the barrel along the shin (knee to foot, where it hangs past the barrel's widest)
    const f = foot.getWorldPosition(new THREE.Vector3()), room = (p: THREE.Vector3) => Math.abs(p.x - center.x) - halfAt(p.y);
    let shin = Infinity;
    for (let t = 0; t <= 1; t += 0.125) shin = Math.min(shin, room(k.clone().lerp(f, t)));
    out.push({ side, knee: room(k), shin });
  }
  return out;
}

/** Seats `rider` (his scene, at his own scale, feet at y 0 facing +z) on the horse: the hip joints over the seat by
 *  his thigh's thickness, bolted to the saddle bone, legs astride. `horse` is posed at rest and placed. */
export function seatRider(rider: THREE.Object3D, horse: THREE.Object3D, saddle: ReturnType<typeof saddleOf>) {
  rider.updateMatrixWorld(true);
  const l = rider.getObjectByName('LeftUpLeg'), r = rider.getObjectByName('RightUpLeg'), kl = rider.getObjectByName('LeftLeg');
  if (!l || !r || !kl) throw new Error('the rider has no leg bones');
  const hips = l.getWorldPosition(new THREE.Vector3()).add(r.getWorldPosition(new THREE.Vector3())).multiplyScalar(0.5);
  const lift = thickness(rider, l, kl) * 0.8;
  rider.position.add(new THREE.Vector3(saddle.at.x - hips.x, saddle.at.y + lift - hips.y, saddle.at.z - hips.z));
  rider.updateMatrixWorld(true);
  const clear = seatLegs(rider, saddle.at, saddle.halfAt);
  saddleBone(horse, saddle.at).attach(rider);
  return clear;
}

/** Ground covered by one cycle of a gait clip, in the horse's units: a hoof's backward speed while it is down (the
 *  slope of its z over the frames where it is lowest), times the clip's length. The rig's clips run in place. */
export function strideOf(root: THREE.Object3D, clip: THREE.AnimationClip) {
  const hoof = root.getObjectByName('front_leg_foot_l') ?? root.getObjectByName('front_leg_ankle_l');
  if (!hoof) throw new Error('the horse has no front hoof bone');
  const mixer = new THREE.AnimationMixer(root), act = mixer.clipAction(clip).play(), n = 60, ys: number[] = [], zs: number[] = [];
  for (let k = 0; k < n; k++) {
    mixer.setTime((clip.duration * k) / n); root.updateMatrixWorld(true);
    const p = root.worldToLocal(hoof.getWorldPosition(new THREE.Vector3()));
    ys.push(p.y); zs.push(p.z);
  }
  act.stop(); mixer.uncacheRoot(root);
  const lo = Math.min(...ys), hi = Math.max(...ys), down = ys.map((y) => y < lo + 0.15 * (hi - lo));
  // the longest run of down frames (wrapping), and its slope by least squares
  let speed = 0, count = 0;
  for (let k = 0; k < n; k++) {
    if (!down[k] || !down[(k + 1) % n]) continue;
    speed += zs[k] - zs[(k + 1) % n]; count++;
  }
  return count ? (speed / count) * n : 0; // per frame step, times the frames in a cycle
}

/** The row a mounted soldier draws while he moves: the gait by his ground speed over his class's (a walk under 0.55,
 *  else a trot, a run when he closes on a foe with his charge ready), at the point of its cycle the ground he has
 *  covered gives (sim phase is 4 per unit of ground, sim.ts), so the hooves keep to the ground at any speed. `scale`
 *  takes the unit to the world. */
export function gaitRow(a: { unit: { moves: Record<string, { duration: number; stride?: number }> }; rowAt(m: string, t: number): number }, phase: number, scale: number, ratio: number, charge: boolean) {
  const mv = a.unit.moves, g = ratio < 0.55 ? 'walk' : charge && mv.run ? 'run' : 'trot', S = (mv[g].stride ?? 0) * scale;
  return a.rowAt(g, S > 0 ? (phase / (4 * S)) * mv[g].duration : 0);
}

/** How long a fallen horse lies before it sinks, and how long the sinking takes (s). */
export const LIE = 1.6, SINK = 0.7;
/** A fallen mounted soldier `age` seconds after he fell (main.ts corpses): his death clip's time (held at its last
 *  frame) and how far he has sunk into the board (to 0.6 of his height `h`), or null once he is gone. */
export function corpseAt(age: number, death: number, h: number) {
  if (age > death + LIE + SINK) return null;
  return { t: THREE.MathUtils.clamp(age, 0, death - 1e-3), drop: (Math.max(0, age - death - LIE) / SINK) * 0.6 * h };
}
